import { For, Show, type Component } from 'solid-js';

import { isOverLimit, moneyOrDash } from '../lib/format.js';
import { AGING_BUCKETS, type CompanyAgingRow, type DashboardReport } from '../lib/types.js';

/**
 * Who owes what, and how late (SPEC FR-3).
 *
 * The balance column is plain `s-text`: `fontWeight` and `emphasis` are NOT attributes of s-text
 * (its observed set is fontVariantNumeric, color, type, dir, accessibilityVisibility, interestFor,
 * tone), so the `<s-text fontWeight="bold">` this replaced styled nothing at all and simply looked
 * like every other cell. Emphasis here comes from `fontVariantNumeric="tabular-nums"`, which lines
 * the digits into columns — the thing that actually makes a money table readable.
 */
export const AgingTable: Component<{
  report: DashboardReport;
  loading: boolean;
  onOpenCompany: (company: CompanyAgingRow) => void;
}> = (props) => {
  return (
    <s-section heading="Aging by company">
      <Show
        when={props.report.companies.length > 0}
        fallback={
          <s-paragraph>
            No net-terms orders yet. Duebook tracks B2B orders placed with payment terms. Once a
            company checks out on terms (or you import history), balances appear here — nothing to
            configure.
          </s-paragraph>
        }
      >
        <s-table loading={props.loading ? 'true' : undefined}>
          <s-table-header-row>
            <s-table-header>Company</s-table-header>
            <For each={AGING_BUCKETS}>
              {(bucket) => <s-table-header>{props.report.bucketLabels[bucket]}</s-table-header>}
            </For>
            <s-table-header>Balance</s-table-header>
            <s-table-header>Credit limit</s-table-header>
          </s-table-header-row>
          <s-table-body>
            <For each={props.report.companies}>
              {(company) => (
                <s-table-row onClick={() => props.onOpenCompany(company)}>
                  <s-table-cell>
                    <s-stack direction="inline" gap="small" alignItems="center">
                      <s-text>{company.name}</s-text>
                      <Show when={isOverLimit(company.openBalanceCents, company.limitCents)}>
                        <s-badge tone="critical">Over limit</s-badge>
                      </Show>
                      <Show when={company.limitCents === null}>
                        <s-badge tone="neutral">No limit</s-badge>
                      </Show>
                    </s-stack>
                  </s-table-cell>
                  <For each={AGING_BUCKETS}>
                    {(bucket) => (
                      <s-table-cell>
                        {/* Overdue buckets read critical: the whole point of the table is to make
                            "how late" visible without doing arithmetic across columns. */}
                        <s-text
                          fontVariantNumeric="tabular-nums"
                          tone={bucket !== 'CURRENT' && company.buckets[bucket] !== '0' ? 'critical' : undefined}
                        >
                          {moneyOrDash(company.buckets[bucket])}
                        </s-text>
                      </s-table-cell>
                    )}
                  </For>
                  <s-table-cell>
                    <s-text fontVariantNumeric="tabular-nums">
                      {moneyOrDash(company.openBalanceCents)}
                    </s-text>
                  </s-table-cell>
                  <s-table-cell>
                    <Show
                      when={company.limitCents !== null}
                      fallback={<s-text tone="subdued">Not set</s-text>}
                    >
                      <s-text fontVariantNumeric="tabular-nums">
                        {moneyOrDash(company.limitCents ?? '0')}
                      </s-text>
                    </Show>
                  </s-table-cell>
                </s-table-row>
              )}
            </For>
          </s-table-body>
        </s-table>
      </Show>
    </s-section>
  );
};
